import { TimeoutError, ConnectionError } from "@/api/api-core";

function initialState() {
    return {
        type: null,
        step: null,
        loading: false,
        previous: []
    };
}

function throwFlowErrors(error) {
    if (error.message === "ZoneDistanceNoTripFound") {
        throw FLOW_ERROR_ZONE_DISTANCE_NO_TRIP;
    }
    if (error.message === "StopIdentityConflict") {
        throw FLOW_ERROR_STOP_IDENTITY_CONFLICT;
    }
    if (error.message === "TicketNotSellable") {
        throw FLOW_ERROR_TICKET_NOT_SELLABLE;
    }
    if (error.message === "ServiceUnavailable") {
        throw FLOW_ERROR_SERVICE_UNAVAILABLE;
    }
    if (error.message === "TooLateToOrderTicket") {
        throw FLOW_ERROR_TOO_LATE_TO_ORDER_TICKET;
    }
    if (error.message === "InvalidTripDepartureInstant") {
        throw FLOW_ERROR_DEPARTURE_DATE_INVALID;
    }
    if (error instanceof TimeoutError || error instanceof ConnectionError) {
        throw FLOW_ERROR_CONNECTION_FAILED;
    }
    throw error;
}

export const FLOW_TYPE_ZONE_TICKET = "FLOW_TYPE_ZONE_TICKET";
export const FLOW_TYPE_FIXED_PRICE_TICKET = "FLOW_TYPE_FIXED_PRICE_TICKET";
export const FLOW_TYPE_RETIREE_TICKET = "FLOW_TYPE_RETIREE_TICKET";

export const FLOW_STEP_TRAVEL_INFORMATION = "FLOW_STEP_TRAVEL_INFORMATION";
export const FLOW_STEP_CUSTOMER_INFORMATION = "FLOW_STEP_CUSTOMER_INFORMATION";
export const FLOW_STEP_CONFIRM_BASKET = "FLOW_STEP_CONFIRM_BASKET";
export const FLOW_STEP_PAYMENT = "FLOW_STEP_PAYMENT";
export const FLOW_STEP_CONFIRM_ORDER = "FLOW_STEP_CONFIRM_ORDER";
export const FLOW_STEP_ORDER_CONFIRMED = "FLOW_STEP_ORDER_CONFIRMED";
export const FLOW_STEP_RETIREE_TICKET_PRODUCT =
    "FLOW_STEP_RETIREE_TICKET_PRODUCT";

export const FLOW_ERROR_NOT_STARTED = "FLOW_ERROR_NOT_STARTED";
export const FLOW_ERROR_LOCATION_INVALID = "FLOW_ERROR_LOCATION_INVALID";
export const FLOW_ERROR_PASSENGER_INVALID = "FLOW_ERROR_PASSENGER_INVALID";
export const FLOW_ERROR_DEPARTURE_DATE_INVALID =
    "FLOW_ERROR_DEPARTURE_DATE_INVALID";
export const FLOW_ERROR_CUSTOMER_INVALID = "FLOW_ERROR_CUSTOMER_INVALID";
export const FLOW_ERROR_TERMS_NOT_ACCEPTED = "FLOW_ERROR_TERMS_NOT_ACCEPTED";
export const FLOW_ERROR_ZONE_DISTANCE_NO_TRIP =
    "FLOW_ERROR_ZONE_DISTANCE_NO_TRIP";
export const FLOW_ERROR_STOP_IDENTITY_CONFLICT =
    "FLOW_ERROR_STOP_IDENTITY_CONFLICT";
export const FLOW_ERROR_TICKET_NOT_SELLABLE = "FLOW_ERROR_TICKET_NOT_SELLABLE";
export const FLOW_ERROR_TOO_LATE_TO_ORDER_TICKET =
    "FLOW_ERROR_TOO_LATE_TO_ORDER_TICKET";
export const FLOW_ERROR_PRODUCT_NOT_AVAILABLE =
    "FLOW_ERROR_PRODUCT_NOT_AVAILABLE";
export const FLOW_ERROR_SERVICE_UNAVAILABLE = "FLOW_ERROR_SERVICE_UNAVAILABLE";
export const FLOW_ERROR_RETIREE_PRODUCT_INVALID =
    "FLOW_ERROR_RETIREE_PRODUCT_INVALID";
export const FLOW_ERROR_CONNECTION_FAILED = "FLOW_ERROR_API_CONNECTION_FAILED";

export const FLOW_LOADING_CREATING_ORDER = "FLOW_LOADING_CREATING_ORDER";
export const FLOW_LOADING_CLAIMING_ORDER = "FLOW_LOADING_CLAIMING_ORDER";
export const FLOW_LOADING_CONFIRMING_ORDER = "FLOW_LOADING_CONFIRMING_ORDER";

export const state = initialState();

export const mutations = {
    reset: state => {
        const s = initialState();
        Object.keys(s).forEach(key => {
            state[key] = s[key];
        });
    },
    clearPrevious: state => {
        state.previous = [];
    },
    addPrevious: (state, step) => {
        state.previous.push(step);
    },
    goBack: (state, step) => {
        let index = -1;
        let destinationStep = null;

        // when we use a negative number to go back in time
        if (step < 0) {
            index = state.previous.length + step;
            destinationStep = state.previous[index];
            // otherwise to try to find a specific step back in time
        } else {
            index = state.previous.indexOf(step);
            destinationStep = state.previous[index];
        }

        if (index >= 0 && destinationStep) {
            state.previous = state.previous.slice(0, index);
            state.step = destinationStep;
        }
    },
    setType: (state, type) => {
        state.type = type;
    },
    setStep: (state, step) => {
        state.step = step;
    },
    addStep: (state, step) => {
        state.previous.push(state.step);
        state.step = step;
    },
    setLoading: (state, loading) => {
        state.loading = loading;
    },
    continueFlow: (state, { type, step }) => {
        state.type = type;
        state.step = step;
    }
};

export const getters = {
    getType: state => state.type,
    getStep: state => state.step,
    getPrevious: state => state.previous,
    getFullStep: state => [state.type, state.step],
    isLoading: state => state.loading,

    // pretty simple at the moment, but could we enhanced to support advanced flow diagrams
    // always return an exported constant, or false if there is no next step
    // this getter should never look into if step is completed or not.
    getNextStep: ({ type, step }) => {
        if (!type || !step) return false;

        switch (step) {
            case FLOW_STEP_TRAVEL_INFORMATION:
                return FLOW_STEP_CUSTOMER_INFORMATION;

            case FLOW_STEP_RETIREE_TICKET_PRODUCT:
                return FLOW_STEP_CUSTOMER_INFORMATION;

            case FLOW_STEP_CUSTOMER_INFORMATION:
                return FLOW_STEP_CONFIRM_BASKET;

            case FLOW_STEP_CONFIRM_BASKET:
                return FLOW_STEP_PAYMENT;

            case FLOW_STEP_PAYMENT:
                return FLOW_STEP_CONFIRM_ORDER;

            case FLOW_STEP_CONFIRM_ORDER:
                return FLOW_STEP_ORDER_CONFIRMED;

            default:
                return false;
        }
    },

    // Do a check on the store state and check if we can let the flow goto next step
    // returns null if no error, or a FLOW_ERROR_ constant if we have an error.
    // This can only do syncronous checks, so no async lookups here. Async errors
    // can be returned in gotoNextStep
    getStepError: ({ type, step }, _getters, _rootState, rootGetters) => {
        if (!type || !step) return FLOW_ERROR_NOT_STARTED;

        // travel information
        if (step === FLOW_STEP_TRAVEL_INFORMATION) {
            if (type === FLOW_TYPE_ZONE_TICKET) {
                if (
                    !rootGetters["location/getFromLocation"] ||
                    !rootGetters["location/getToLocation"] ||
                    rootGetters["location/hasDuplicates"]
                ) {
                    return FLOW_ERROR_LOCATION_INVALID;
                }
            }

            if (rootGetters["passenger/getPassengerCount"] === 0) {
                return FLOW_ERROR_LOCATION_INVALID;
            }

            if (!rootGetters["date/isSelectedDepartureDateValid"]) {
                return FLOW_ERROR_DEPARTURE_DATE_INVALID;
            }
        }

        if (step === FLOW_STEP_RETIREE_TICKET_PRODUCT) {
            if (!rootGetters["products/retireeticket/isValid"]) {
                return FLOW_ERROR_RETIREE_PRODUCT_INVALID;
            }
        }

        // customer information
        if (step === FLOW_STEP_CUSTOMER_INFORMATION) {
            if (!rootGetters["customer/isValid"]) {
                return FLOW_ERROR_CUSTOMER_INVALID;
            }
        }

        // confirm basket
        if (step === FLOW_STEP_CONFIRM_BASKET) {
            if (!rootGetters["customer/getTermsAccept"]) {
                return FLOW_ERROR_TERMS_NOT_ACCEPTED;
            }

            if (
                !rootGetters["customer/getDeliveryTermsAccept"] &&
                rootGetters["order/getDeliveryMethod"] === "snailmail"
            ) {
                return FLOW_ERROR_TERMS_NOT_ACCEPTED;
            }
        }

        return null;
    },

    // just some sugar around getStepError, when you only need a boolean
    isStepValid: (_state, getters) => Boolean(getters.getStepError) === false
};

export const actions = {
    resetFlow: context => {
        context.commit("reset");
        context.commit("order/reset", null, { root: true });
        context
            .dispatch(
                "date/setServerTime",
                { setDepartureDate: true },
                { root: true }
            )
            .catch(throwFlowErrors);

        // enable these if you want the reset flow to clear more state
        // context.commit('customer/resetCustomer', null, { root: true })
        // context.commit('location/unsetLocations', null, { root: true })
        // context.commit('passenger/clearPassengers', null, { root: true })
    },

    startZoneTicketFlow: context => {
        context.dispatch("resetFlow");
        context.commit("clearPrevious");
        context.commit("setType", FLOW_TYPE_ZONE_TICKET);
        context.commit("setStep", FLOW_STEP_TRAVEL_INFORMATION);
        return context
            .dispatch(
                "order/prepare",
                {
                    productType:
                        context.rootState.products.zoneticket.info.identifier
                },
                { root: true }
            )
            .catch(throwFlowErrors);
    },

    startFixedPriceTicketFlow: (context, { product }) => {
        context.dispatch("resetFlow");
        context.commit("clearPrevious");
        context.commit("setType", FLOW_TYPE_FIXED_PRICE_TICKET);

        if (product) {
            context.commit("setStep", FLOW_STEP_TRAVEL_INFORMATION);
            return context
                .dispatch(
                    "order/prepare",
                    {
                        productType:
                            context.rootState.products.fixedpriceticket.info
                                .identifier,
                        product
                    },
                    { root: true }
                )
                .catch(throwFlowErrors);
        }

        return Promise.resolve(true);
    },

    startRetireeTicketFlow: (context, { municipalityId, productVariantId }) => {
        context.dispatch("resetFlow");
        context.commit("clearPrevious");
        context.commit("setType", FLOW_TYPE_RETIREE_TICKET);

        if (municipalityId) {
            context.commit("setStep", FLOW_STEP_RETIREE_TICKET_PRODUCT);
            return context
                .dispatch(
                    "order/prepare",
                    {
                        productType:
                            context.rootState.products.retireeticket.info
                                .identifier,
                        municipalityId,
                        productVariantId
                    },
                    { root: true }
                )
                .catch(throwFlowErrors);
        }

        return Promise.resolve(true);
    },

    // any async/sync logic happening between steps should happen here.
    // Always return a promise, and it is allowed to reject with FLOW_ERROR_ constants
    gotoNextStep: context => {
        const stepError = context.getters.getStepError;
        if (stepError) return Promise.reject(stepError);

        // if flow has ended, we handle it gracefully without throwing errors
        const currentStep = context.getters.getStep;
        let nextStep = context.getters.getNextStep;
        if (!nextStep || currentStep === nextStep) return Promise.resolve(null);

        // below when going out of loading state, we are putting in a few milliseconds
        // to prevent flashing when route changes, because vue watch will be run in text ticks,
        // UI hacks should'nt belong here, so open for suggestions!

        switch (currentStep) {
            case FLOW_STEP_TRAVEL_INFORMATION:
            case FLOW_STEP_RETIREE_TICKET_PRODUCT:
                context.commit("setLoading", FLOW_LOADING_CREATING_ORDER);
                return context
                    .dispatch("order/create", null, { root: true })
                    .then(() => {
                        context.commit("addStep", nextStep);
                        return nextStep;
                    })
                    .catch(throwFlowErrors)
                    .finally(() => {
                        setTimeout(
                            () => context.commit("setLoading", false),
                            50
                        );
                    });

            case FLOW_STEP_CONFIRM_BASKET:
                // claim basket when leaving confirm basket step
                context.commit("setLoading", FLOW_LOADING_CLAIMING_ORDER);
                return context
                    .dispatch("order/claim", null, { root: true })
                    .then(() => {
                        context.commit("addStep", nextStep);
                        return nextStep;
                    })
                    .catch(throwFlowErrors)
                    .finally(() => {
                        setTimeout(
                            () => context.commit("setLoading", false),
                            50
                        );
                    });

            case FLOW_STEP_CONFIRM_ORDER:
                // confirm order when leaving confirm order step
                context.commit("setLoading", FLOW_LOADING_CONFIRMING_ORDER);
                return context
                    .dispatch("order/confirm", null, { root: true })
                    .then(() => {
                        context.commit("addStep", nextStep);
                        return nextStep;
                    })
                    .catch(throwFlowErrors)
                    .finally(() => {
                        setTimeout(
                            () => context.commit("setLoading", false),
                            50
                        );
                    });
        }

        // default flow handler, remember to return early if something speciel should happen
        context.commit("addStep", nextStep);
        return Promise.resolve(nextStep);
    }
};
